import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';

const CalendarView = ({ 
  appointments, 
  technicians,
  onAppointmentClick,
  onDateChange,
  selectedDate,
  timeOffEvents // Nouvelle prop pour les absences
}) => {
  const [viewMode, setViewMode] = useState('day');
  const [currentDate, setCurrentDate] = useState(selectedDate || new Date());
  const [timeOffForDate, setTimeOffForDate] = useState([]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 8); // 8h à 19h

  // Charger les absences pour la date sélectionnée
  useEffect(() => {
    const loadTimeOffForDate = async () => {
      if (!timeOffEvents) return;
      
      const dateStr = currentDate.toISOString().split('T')[0];
      
      // Filtrer les absences qui concernent la date courante
      const filtered = timeOffEvents.filter(event => {
        const startDate = new Date(event.start_date).toISOString().split('T')[0];
        const endDate = event.end_date 
          ? new Date(event.end_date).toISOString().split('T')[0]
          : startDate;
        
        return dateStr >= startDate && dateStr <= endDate;
      });
      
      setTimeOffForDate(filtered);
    };

    loadTimeOffForDate();
  }, [currentDate, timeOffEvents]);

  // Obtenir les appointments pour une heure et un technicien donnés
  const getAppointmentsForHour = (hour, technicianId) => {
    return appointments?.filter(apt => {
      const aptHour = parseInt(apt?.scheduledTime?.split(':')?.[0]);
      return aptHour === hour && apt?.technicianId === technicianId;
    });
  };

  // Vérifier si un technicien a une absence à une heure donnée
  const getTimeOffForTechnicianAndHour = (technicianId, hour) => {
    const dateStr = currentDate.toISOString().split('T')[0];
    
    return timeOffForDate.find(event => {
      if (event.technician_id !== technicianId) return false;
      
      // Absence toute la journée
      if (event.is_all_day) return true;
      
      // Absence à heures spécifiques
      if (event.start_time && event.end_time) {
        const eventStartHour = parseInt(event.start_time.split(':')[0]);
        const eventEndHour = parseInt(event.end_time.split(':')[0]);
        
        // Vérifier si l'heure courante est dans la plage d'absence
        return hour >= eventStartHour && hour < eventEndHour;
      }
      
      return false;
    });
  };

  // Formater la date pour l'affichage
  const formatDate = (date) => {
    return new Intl.DateTimeFormat('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    }).format(date);
  };

  // Navigation entre les dates
  const navigateDate = (direction) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'day') {
      newDate.setDate(newDate.getDate() + direction);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    }
    setCurrentDate(newDate);
    onDateChange(newDate);
  };

  // Aller à aujourd'hui
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    onDateChange(today);
  };

  // Obtenir la couleur selon l'urgence
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'urgent':
      case 'emergency':
        return 'bg-error/20 border-error text-error hover:bg-error/30';
      case 'today':
      case 'high':
        return 'bg-warning/20 border-warning text-warning hover:bg-warning/30';
      case 'scheduled':
      case 'normal':
        return 'bg-primary/20 border-primary text-primary hover:bg-primary/30';
      case 'low':
        return 'bg-success/20 border-success text-success hover:bg-success/30';
      default:
        return 'bg-primary/20 border-primary text-primary hover:bg-primary/30';
    }
  };

  // Obtenir le style pour une case d'absence
  const getTimeOffStyle = (timeOff) => {
    return 'bg-error/10 border-2 border-error/50 text-error hover:bg-error/20 cursor-not-allowed';
  };

  // Obtenir le label du type de service
  const getServiceTypeLabel = (serviceType) => {
    const types = {
      'leak': 'Fuite',
      'installation': 'Installation',
      'unclog': 'Débouchage',
      'repair': 'Réparation',
      'maintenance': 'Maintenance',
      'heating': 'Chauffage',
      'emergency': 'Urgence'
    };
    return types[serviceType] || serviceType;
  };

  // Obtenir le libellé du type d'absence
  const getAbsenceTypeLabel = (type) => {
    const types = {
      'vacation': 'Congés',
      'sick': 'Maladie',
      'authorization': 'Autorisation',
      'training': 'Formation',
      'meeting': 'Réunion',
      'personal': 'Raison personnelle',
      'other': 'Autre'
    };
    return types[type] || type;
  };

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-foreground">Planning des interventions</h2>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goToToday}
            >
              Aujourd'hui
            </Button>
            <Button
              variant={viewMode === 'day' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('day')}
            >
              Jour
            </Button>
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
            >
              Semaine
            </Button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronLeft"
            onClick={() => navigateDate(-1)}
            aria-label="Date précédente"
          />
          <span className="text-sm font-medium text-foreground capitalize">
            {formatDate(currentDate)}
          </span>
          <Button
            variant="outline"
            size="sm"
            iconName="ChevronRight"
            onClick={() => navigateDate(1)}
            aria-label="Date suivante"
          />
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="overflow-x-auto">
        <div className="min-w-[800px]">
          {/* Header Row - Technicians */}
          <div className="grid grid-cols-[100px_repeat(auto-fit,minmax(150px,1fr))] border-b border-border">
            <div className="p-3 bg-muted font-medium text-sm text-foreground sticky left-0 z-10">
              Heure
            </div>
            {technicians?.map(tech => {
              // Vérifier si le technicien a une absence aujourd'hui
              const hasTimeOffToday = timeOffForDate.some(
                event => event.technician_id === tech.id && event.is_all_day
              );
              
              return (
                <div key={tech?.id} className={`p-3 bg-muted border-l border-border ${
                  hasTimeOffToday ? 'bg-error/10' : ''
                }`}>
                  <div className="font-medium text-sm text-foreground truncate flex items-center gap-1" title={tech?.name}>
                    {tech?.name}
                    {hasTimeOffToday && (
                      <span className="text-xs px-1 py-0.5 bg-error/20 text-error rounded-full">
                        Absent
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {tech?.specialty}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <div className={`w-2 h-2 rounded-full ${
                      hasTimeOffToday ? 'bg-error' :
                      tech?.status === 'available' ? 'bg-success' :
                      tech?.status === 'busy' || tech?.status === 'in_progress' ? 'bg-error' :
                      tech?.status === 'en_route' ? 'bg-warning' :
                      'bg-muted-foreground'
                    }`} />
                    <span className="text-xs text-muted-foreground">
                      {tech?.todayServices || 0}/{tech?.maxServices || 8}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Time Slots */}
          <div className="max-h-[600px] overflow-y-auto scrollbar-thin">
            {hours?.map(hour => (
              <div 
                key={hour} 
                className="grid grid-cols-[100px_repeat(auto-fit,minmax(150px,1fr))] border-b border-border min-h-[80px]"
              >
                {/* Hour Label */}
                <div className="p-3 bg-muted/50 font-medium text-sm text-foreground sticky left-0 z-10">
                  {hour}:00
                </div>

                {/* Appointments for each technician */}
                {technicians?.map(tech => {
                  const hourAppointments = getAppointmentsForHour(hour, tech?.id);
                  const timeOff = getTimeOffForTechnicianAndHour(tech?.id, hour);
                  
                  return (
                    <div key={tech?.id} className="p-2 border-l border-border relative">
                      {timeOff ? (
                        // Afficher l'absence
                        <div
                          className={`w-full p-2 rounded text-left text-xs mb-1 ${getTimeOffStyle(timeOff)}`}
                          title={`${getAbsenceTypeLabel(timeOff.absence_type)} - ${timeOff.reason || 'Absence'}`}
                        >
                          <div className="font-medium truncate flex items-center gap-1">
                            <span>🔴</span>
                            {getAbsenceTypeLabel(timeOff.absence_type)}
                          </div>
                          <div className="truncate opacity-80 text-xs">
                            {timeOff.reason || 'Indisponible'}
                          </div>
                          {!timeOff.is_all_day && timeOff.start_time && timeOff.end_time && (
                            <div className="text-xs opacity-70 mt-1">
                              {timeOff.start_time?.substring(0,5)} - {timeOff.end_time?.substring(0,5)}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Afficher les appointments normaux
                        hourAppointments?.map((apt) => (
                          <button
                            key={apt?.id}
                            onClick={() => onAppointmentClick(apt)}
                            className={`w-full p-2 rounded text-left text-xs mb-1 transition-all hover:shadow-md border ${
                              getUrgencyColor(apt?.urgency)
                            }`}
                            title={`${getServiceTypeLabel(apt?.serviceType)} - ${apt?.customerName}`}
                          >
                            <div className="font-medium truncate">
                              {getServiceTypeLabel(apt?.serviceType)}
                            </div>
                            <div className="truncate opacity-80 text-xs">
                              {apt?.customerName}
                            </div>
                            {apt?.scheduledTime && (
                              <div className="text-xs opacity-70 mt-1">
                                {apt?.scheduledTime?.substring(0, 5)}
                              </div>
                            )}
                          </button>
                        ))
                      )}
                      {!timeOff && hourAppointments?.length === 0 && (
                        <div className="h-full min-h-[60px] flex items-center justify-center opacity-0 hover:opacity-30 transition-opacity">
                          <span className="text-xs text-muted-foreground">+</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-border bg-muted/30">
        <div className="flex flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-error/20 border border-error" />
            <span className="text-muted-foreground">Urgent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-warning/20 border border-warning" />
            <span className="text-muted-foreground">Aujourd'hui</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-primary/20 border border-primary" />
            <span className="text-muted-foreground">Planifié</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-success/20 border border-success" />
            <span className="text-muted-foreground">Basse priorité</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded bg-error/20 border-2 border-error/50" />
            <span className="text-muted-foreground">Absence / Indisponible</span>
          </div>
        </div>
      </div>

      {/* Empty State */}
      {(!technicians || technicians.length === 0) && (
        <div className="p-8 text-center">
          <p className="text-muted-foreground">Aucun technicien disponible</p>
        </div>
      )}
    </div>
  );
};

export default CalendarView;